import { useCallback, useEffect, useMemo, useState } from 'react';
import { OutletContext } from 'applications/common/routing/outlet-context.js';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import { useDispatch, useSelector } from 'react-redux';
import MojitoServices from 'mojito/services';
import { isEmpty } from 'mojito/utils';
import { selectPreviousRoute } from 'applications/common/stores/router/selectors.js';

const { useLocalizedParams } = MojitoPresentation.Hooks;
const { SingleLeagueCouponTypes } = MojitoModules.SingleLeagueCoupon;
const { BannerPromotions, PromotionUtils } = MojitoModules.Promotions;
const { useContainerItems, useEventGroup, useTournamentRequest } = MojitoModules.Common.Hooks;
const SportsMenu = MojitoModules.SportsMenu.module;
const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const QuickLeaguesNavigation = MojitoModules.QuickLeaguesNavigation;
const SportTabSelector = MojitoModules.SportTabSelector.module;
const {
    types: { TYPE_TABS },
} = MojitoModules.SportTabSelector;
const {
    FlexPane,
    GlobalLinks,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    DocumentHead,
    DocumentHeadTypes,
    PageHeader,
    QuickNavigationButton,
    EmptyContent,
    QuickNavigationPane,
} = MojitoPresentation.Components;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { COUPONS } = SingleLeagueCouponTypes;
const {
    actions: userSettingsActions,
    types: UserSettingsTypes,
    selectors: userSettingsSelectors,
} = MojitoServices.UserSettings;
const { selectEventGroupState } = MojitoServices.EventGroups.selectors;
const { selectTournamentState } = MojitoServices.SportsContent.Tournaments.selectors;
const { selectOnboarding } = userSettingsSelectors;
const { LEAGUES_QUICK_NAVIGATION } = UserSettingsTypes.ONBOARDING_TYPES;
const { CONTENT_STATE } = MojitoServices.Common.types;

export default function SingleLeaguePage(props) {
    const { sportId, typeId } = useLocalizedParams();
    const {
        mojitoTools: { appContext, config, emitPerformanceMark, l10n, stringResolver, style },
        pageType,
        children,
    } = props;
    const {
        primaryContentContainer,
        secondaryContentContainer,
        leagueViewWrapper,
        eventCardsCarousel,
        emptyContent,
    } = config;
    const getListId = (parentId, eventType) => `${parentId}-${eventType}`;
    const sportClassesListId = getListId(sportId, pageType);
    const sportClasses = useContainerItems(sportClassesListId);
    const typesById = (sportClasses || []).reduce((acc, sportClass) => {
        sportClass.items.forEach(item => {
            acc[item.id] = { ...item, sportClassName: sportClass.name, classId: sportClass.id };
        });
        return acc;
    }, {});
    const type = typesById[typeId];
    const onboarding = useSelector(selectOnboarding);
    const { [LEAGUES_QUICK_NAVIGATION]: quickNavigationOnboarding } = onboarding;
    const isCompetitionsPage = pageType === COUPONS.COMPETITIONS;
    const isOutrightPage = pageType === COUPONS.OUTRIGHTS;
    useTournamentRequest(isOutrightPage ? undefined : typeId);
    const tournamentState = useSelector(state =>
        isOutrightPage || !typeId ? CONTENT_STATE.UNKNOWN : selectTournamentState(typeId, state)
    );
    const isTournamentUnavailable = tournamentState === CONTENT_STATE.UNAVAILABLE;
    const isTablePageURL = appContext.isContentShown(
        NavigationPathBuilder.toSportTableLeague(sportId, typeId)
    );
    const hasTableTab = tournamentState === CONTENT_STATE.AVAILABLE && !isTournamentUnavailable;
    const shouldExcludeSportTab = useCallback(
        tabName => {
            const isTableTab = tabName === 'table';
            if (isTableTab) {
                return !hasTableTab;
            }

            const isMatchesTab = tabName === 'matches';
            const isTypeTabUnavailable = !type || isEmpty(type[tabName]);

            return !isMatchesTab && isTypeTabUnavailable;
        },
        [hasTableTab, type]
    );
    const eventGroupId = isOutrightPage
        ? appContext.subscriptionResolver.allOutrightEvents(typeId)
        : appContext.subscriptionResolver.allMatchEvents(typeId);
    const eventGroup = useEventGroup(eventGroupId);
    const groupsLoadingState = useSelector(state => selectEventGroupState(eventGroupId, state));

    const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;
    const documentHeadMetaFactory = new metaFactory(l10n);
    const previousRoute = useSelector(state => selectPreviousRoute(state));

    // Determining child modules loading state
    const [loadDone, setLoadDone] = useState(false);

    const [isQuickNavigationPopupOpen, setIsQuickNavigationPopupOpen] = useState(false);

    useEffect(() => {
        emitPerformanceMark('moduleRendered', loadDone);
    }, [emitPerformanceMark, loadDone]);

    const onChildLoaded = useCallback(() => {
        setLoadDone(true);
    }, []);

    const outletContextValue = useMemo(
        () => ({
            sportId,
            typeId,
            pageType,
            type,
            onDataLoad: onChildLoaded,
        }),
        [sportId, typeId, pageType, type, onChildLoaded]
    );

    const renderLeftPromotions = () => {
        const { leftPromotions: promotionsConfig } = config;
        if (!PromotionUtils.hasLocationId(promotionsConfig)) {
            return;
        }

        return <BannerPromotions config={promotionsConfig} />;
    };

    const resolvePageId = () => {
        if (isCompetitionsPage) {
            return DocumentHeadTypes.PAGE_ID.MATCH_SPORT_COMPETITIONS;
        } else if (isOutrightPage) {
            return DocumentHeadTypes.PAGE_ID.MATCH_SPORT_OUTRIGHTS;
        }
    };
    const renderDocumentHead = (sport, type) => {
        const hierarchy = [sport, type].filter(Boolean);
        const localizedParams = new Map(hierarchy.map(({ id, name }) => [id, name]));
        const headData = {
            title: '$SINGLE_LEAGUE_COUPON.PAGE_TITLE',
            titleSeparator: '$SINGLE_LEAGUE_COUPON.PAGE_TITLE_SEPARATOR',
            keywords: '$SINGLE_LEAGUE_COUPON.PAGE_KEYWORDS',
            keywordsSeparator: '$SINGLE_LEAGUE_COUPON.PAGE_KEYWORDS_SEPARATOR',
            description: '$SINGLE_LEAGUE_COUPON.PAGE_DESCRIPTION',
            descriptionSeparator: '$SINGLE_LEAGUE_COUPON.PAGE_DESCRIPTION_SEPARATOR',
            canonical: '$SINGLE_LEAGUE_COUPON.PAGE_CANONICAL',
            canonicalSeparator: '$SINGLE_LEAGUE_COUPON.PAGE_CANONICAL_SEPARATOR',
            localizedParams,
            params: {
                sportId: sport.id,
                type: type && type.id,
                pageId: resolvePageId(),
            },
        };

        return <DocumentHead data={headData} metaFactory={documentHeadMetaFactory} />;
    };

    const dispatch = useDispatch();

    const renderPageHeader = () => {
        const sportName = appContext.sportName(sportId);
        const documentHead = renderDocumentHead({ id: sportId, name: sportName }, type);

        if (config.pageHeader.hide) {
            return documentHead;
        }

        const onTooltipClick = () => {
            dispatch(
                userSettingsActions.updateOnboardingProgress({
                    [LEAGUES_QUICK_NAVIGATION]: { passed: true },
                })
            );
        };

        const classId = eventGroup?.[0]?.classId ?? type?.classId;

        const onQuickNavigationClick = () => {
            setIsQuickNavigationPopupOpen(true);
            appContext.analyticsEmitter.emitAnalytics('leaguesQuickNavigationOpened');

            if (!quickNavigationOnboarding?.passed) {
                onTooltipClick();
            }
        };
        const closePopup = () => {
            setIsQuickNavigationPopupOpen(false);
            appContext.analyticsEmitter.emitAnalytics('leaguesQuickNavigationClosed');
        };

        const navigateInsideSportsbook = () => {
            isOutrightPage
                ? IntentActionTrigger.showSportOutrights(sportId, typeId, false)
                : IntentActionTrigger.showSport(sportId, false);
        };

        const onBackClicked = () => {
            previousRoute ? window.history.back() : navigateInsideSportsbook();
        };

        const iconSrc = config.sportIcons[sportId] || config.sportIcons.defaultIcon;
        const hasType = Boolean(type);
        const tooltipText =
            isOutrightPage || quickNavigationOnboarding?.passed || !hasType
                ? undefined
                : stringResolver.resolveString('$SINGLE_LEAGUE_COUPON.QUICK_NAVIGATION_TOOLTIP');

        return (
            <>
                {documentHead}
                <FlexPane config={config.pageHeaderContainer}>
                    <PageHeader
                        showBackButton={hasType}
                        iconSrc={hasType ? undefined : iconSrc}
                        config={config.pageHeaderConfig}
                        onBackClicked={onBackClicked}
                        tooltipText={tooltipText}
                        onTooltipClick={onTooltipClick}
                    >
                        {type && (
                            <QuickNavigationButton
                                config={config.quickNavigationButton}
                                title={type.sportClassName}
                                subTitle={type.name}
                                disabled={!isCompetitionsPage}
                                onClick={onQuickNavigationClick}
                            />
                        )}
                        <QuickNavigationPane
                            config={config.quickNavigationPane}
                            onOutsideClick={closePopup}
                            isEnabled={isQuickNavigationPopupOpen}
                        >
                            <QuickLeaguesNavigation
                                config={config.quickLeaguesNavigation}
                                sportClassId={classId}
                                sportId={sportId}
                                selectedTypeId={typeId}
                                onItemClick={closePopup}
                            />
                        </QuickNavigationPane>
                        {!isOutrightPage && (
                            <SportTabSelector
                                tabs={TYPE_TABS}
                                shouldExcludeTab={shouldExcludeSportTab}
                                config={config.sportTabSelector}
                                routeParams={{ sportId, typeId }}
                            />
                        )}
                    </PageHeader>
                </FlexPane>
            </>
        );
    };

    const isGroupAvailable = groupsLoadingState === CONTENT_STATE.AVAILABLE;
    const isGroupNotAvailable = groupsLoadingState === CONTENT_STATE.UNAVAILABLE;

    const toMenuNavigation = menuId => {
        switch (menuId) {
            case 'priceBoosts':
                return NavigationPathBuilder.toSportPriceBoostsLeague(sportId, typeId);
            case 'preBuiltBetBuilders':
                return NavigationPathBuilder.toSportPreBetBuildersLeague(sportId, typeId);
        }
    };

    const availableMenuNavigations = Object.keys(type || {})
        .map(menuId => !isEmpty(type[menuId]) && toMenuNavigation(menuId))
        .filter(Boolean);

    const isMatchesPageURL = appContext.isContentShown(
        NavigationPathBuilder.toSportMatchesLeague(sportId, typeId)
    );

    // Fix EmptyContent when redirecting from default page
    const isCompetitionsPageUrl = appContext.isContentShown(
        NavigationPathBuilder.toSportCompetitionsLeague(sportId, typeId),
        true
    );

    const isOutrightsPageUrl = appContext.isContentShown(
        NavigationPathBuilder.toSportOutrightsLeague(sportId, typeId),
        true
    );

    // Table data is on-demand (custom tournament slice) and does not depend on the league
    // match-events coupon. Treat the table route as having content while the tournament
    // subscription is still resolving, not only after it lands in the store.
    // However, if the tournament explicitly resolves to UNAVAILABLE, the table route has no content.
    const hasTableRouteContent = isTablePageURL && !isTournamentUnavailable;
    const canShowLeagueShell = isGroupAvailable || hasTableRouteContent || hasTableTab;

    const shouldRenderMenuNavigation = availableMenuNavigations.length > 0;

    const hasContent =
        isMatchesPageURL ||
        isCompetitionsPageUrl ||
        isOutrightsPageUrl ||
        hasTableRouteContent ||
        availableMenuNavigations.some(navigation => appContext.isContentShown(navigation));

    return (
        <PageSplitLayout>
            {/* Primary content */}
            <FlexPane config={primaryContentContainer}>
                {canShowLeagueShell && (
                    <>
                        {renderPageHeader()}
                        <FlexPane
                            config={
                                shouldRenderMenuNavigation || !isOutrightPage
                                    ? style.leagueViewWithTabsWrapper
                                    : leagueViewWrapper
                            }
                        >
                            {(isMatchesPageURL || isOutrightPage) && (
                                <EventCardsCarousel
                                    sportId={sportId}
                                    pageId={typeId}
                                    config={eventCardsCarousel}
                                />
                            )}
                            <OutletContext.Provider value={outletContextValue}>
                                {children}
                            </OutletContext.Provider>
                        </FlexPane>
                    </>
                )}
                {((isGroupNotAvailable && !hasTableRouteContent) || (!hasContent && type)) && (
                    <EmptyContent config={emptyContent} />
                )}
            </FlexPane>
            {/* Secondary content */}
            <FlexPane config={secondaryContentContainer}>
                <SportsMenu selectedSportId={sportId} />
                <GlobalLinks />
                {renderLeftPromotions()}
            </FlexPane>
        </PageSplitLayout>
    );
}

SingleLeaguePage.getStyle = (config, applicationmode, merge) => ({
    leagueViewWithTabsWrapper: merge(config.leagueViewWrapper, config.leagueViewWithTabsWrapper),
});
